import React, { useMemo } from 'react';
import { StyleSheet, View, Platform, ScrollView } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import MediaCard from '@/components/ChatInput/MediaCard';
import FileParamGhostCard from '@/components/ChatInput/FileParamGhostCard';
import FilePreparationStatus from '@/components/ChatInput/FilePreparationStatus';
import { isFileParam } from '@/components/ChatInput/fileParamUtils';
import { getShadowThemeStyles } from '@/constants/Colors';

const createStyles = (colors, isDetached, themeStyles) => StyleSheet.create({
  filesContainer: {
    borderBottomWidth: isDetached ? 1 : 2,
    borderColor: themeStyles.borderColor,
  },
  filesContainerBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.35,
    backgroundColor: colors.solid_color_block,
  },
  filesContainerInner: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    gap: 8,
    position: 'relative',
    zIndex: 1,
    padding: 8,
    width: '100%',
    ...(Platform.OS === 'web' ? {
      display: 'flex',
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'none',
    } : {}),
  },
  nativeScrollView: {
    padding: 8,
  },
  nativeScrollContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingTop: Platform.OS === 'macos' ? 15 : 0,
    paddingLeft: Platform.OS === 'macos' ? 15 : 0,
  },
  mediaCardWrapper: {
    flexShrink: 0,
  },
});

/**
 * Returns true if `file` matches the type expected by `param`'s file
 * validation. Used to decide whether to suppress the ghost card. Mirrors
 * the broad-bucket matcher used by the send-time guard in ChatInput.
 */
const fileSatisfiesParam = (file, param) => {
  const expected = param?.validation?.file?.fileType;
  if (!expected || expected === 'any') return true;
  const ftype = file?.fileType;
  if (ftype === expected) return true;
  const mime = (file?.mimeType || file?.type || '').toString();
  if (expected === 'image' && mime.startsWith('image/')) return true;
  if (expected === 'audio' && mime.startsWith('audio/')) return true;
  if (expected === 'video' && mime.startsWith('video/')) return true;
  // `document` covers PDFs, plain text, Office docs, etc. There's no
  // top-level `document/*` MIME prefix, so we match by common prefixes.
  if (expected === 'document' && (
    mime === 'application/pdf'
    || mime.startsWith('text/')
    || mime.startsWith('application/msword')
    || mime.startsWith('application/vnd.')
    || mime.startsWith('application/rtf')
  )) return true;
  return false;
};

/**
 * Pulls all file-typed custom parameters out of `parameters` that are required
 * and don't yet have a matching file attached. These render as ghost cards.
 */
const computeGhostParams = (parameters, files) => {
  if (!Array.isArray(parameters)) return [];
  const fileList = Array.isArray(files) ? files : [];
  const seenIds = new Set();
  return parameters.filter((p) => {
    if (!isFileParam(p)) return false;
    if (seenIds.has(p.id)) return false;
    seenIds.add(p.id);
    // Hide the ghost once a matching file is in the attachment row.
    if (fileList.some((f) => fileSatisfiesParam(f, p))) return false;
    return true;
  });
};

export const ChatInputFilesContainer = ({
  files,
  onDeleteFile,
  isDetached = false,
  parameters,
}) => {
  const { colors, theme } = useTheme();
  const themeStyles = getShadowThemeStyles(theme);
  const styles = useMemo(() => createStyles(colors, isDetached, themeStyles), [colors, isDetached, themeStyles]);

  const ghostParams = useMemo(
    () => computeGhostParams(parameters, files),
    [parameters, files]
  );

  if (!files?.length && !ghostParams.length) return null;

  const renderFiles = () => (Array.isArray(files) ? files : []).map((file, index) => (
    <View key={`${file?.id || 'file'}-${index}`} style={styles.mediaCardWrapper}>
      <MediaCard file={file} onDelete={onDeleteFile} />
    </View>
  ));

  const renderGhosts = () => ghostParams.map((param) => (
    <View key={`ghost-${param.id}`} style={styles.mediaCardWrapper}>
      <FileParamGhostCard param={param} />
    </View>
  ));

  return (
    <View style={styles.filesContainer}>
      <View style={styles.filesContainerBackground} />
      {Platform.OS === 'web' ? (
        <View style={styles.filesContainerInner}>
          {renderFiles()}
          {renderGhosts()}
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.nativeScrollView}
          contentContainerStyle={styles.nativeScrollContent}
        >
          {renderFiles()}
          {renderGhosts()}
        </ScrollView>
      )}
      <FilePreparationStatus files={files} />
    </View>
  );
};

export default React.memo(ChatInputFilesContainer);
